/**
 * CoursePicker: choose one course of the network (plan docs/plans/lms-plan.md
 * 8.2 step 1 "pick the course (or scenario)", 5.3 profile requirements,
 * 18.17; WP-43; reused by WP-45 and WP-46).
 *
 * Props (stable):
 *   value           string | null                                       the chosen course id
 *   onChange        (id: string | null, course: AdminCourseItem | null) => void
 *   publishedOnly?  boolean   only courses with a published version (default true: nothing is sent before a
 *                             person publishes it, G8; a profile may require a draft course, so Groups passes false)
 *   categories?     CourseCategory[]  only these categories (default every one)
 *   excludeIds?     string[]  courses not to offer (for example the ones a profile already requires)
 *   label?          string    the visible label and the select's accessible name (default "Course")
 *   disabled?       boolean
 *
 * Reads GET /courses?all=1 (api/assignments.ts listAdminCourses, the admin
 * view of 18.17) once on mount. Retired courses are never offered (8.1: they
 * can be neither assigned nor required). A native select grouped by category
 * (optgroup); a course with no published version reads "(not published yet)".
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import FilterSelect from '../../../ui/FilterSelect';
import { apiErrorMessage } from '../../api/client';
import { listAdminCourses } from '../../api/assignments';
import type { AdminCourseItem } from '../../api/assignments';
import { COURSE_CATEGORIES } from '../../content/types';
import type { CourseCategory } from '../../content/types';
import { ErrorNote, labelCls } from './shared';

export interface CoursePickerProps {
  value: string | null;
  onChange: (id: string | null, course: AdminCourseItem | null) => void;
  publishedOnly?: boolean;
  categories?: CourseCategory[];
  excludeIds?: string[];
  label?: string;
  disabled?: boolean;
}

/** The catalog's categories in words (plan section 13 tab 2). */
export const CATEGORY_LABELS: Record<CourseCategory, string> = {
  compliance: 'Compliance',
  clinical_skills: 'Clinical skills',
  best_practices: 'Best practices (scenarios)',
  sop: 'SOPs',
  platform: 'Platform',
  custom: 'Custom',
};

export function CoursePicker({
  value,
  onChange,
  publishedOnly = true,
  categories,
  excludeIds,
  label = 'Course',
  disabled = false,
}: CoursePickerProps) {
  const [courses, setCourses] = useState<AdminCourseItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listAdminCourses()
      .then((items) => {
        if (!cancelled) setCourses(items);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Could not load the courses.'));
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const offered = useMemo(() => {
    const skip = new Set(excludeIds ?? []);
    return (courses ?? []).filter(
      (c) =>
        c.is_active !== false &&
        (!publishedOnly || c.current_version !== null) &&
        (!categories || categories.includes(c.category)) &&
        (!skip.has(c.id) || c.id === value)
    );
  }, [courses, excludeIds, publishedOnly, categories, value]);

  const groups = useMemo(
    () =>
      COURSE_CATEGORIES.map((category) => ({
        category,
        items: offered.filter((c) => c.category === category),
      })).filter((g) => g.items.length > 0),
    [offered]
  );

  const choose = (next: string) => {
    if (!next) {
      onChange(null, null);
      return;
    }
    onChange(next, offered.find((c) => c.id === next) ?? null);
  };

  return (
    <div data-testid="course-picker">
      <div className={labelCls} aria-hidden="true">
        {label}
      </div>
      {error ? (
        <ErrorNote>
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </button>
        </ErrorNote>
      ) : courses === null ? (
        <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
          Loading courses
        </p>
      ) : offered.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {publishedOnly ? "No published course to choose yet. Publish one under Catalog first." : "No course to choose."}
        </p>
      ) : (
        <FilterSelect value={value ?? ''} onChange={choose} label={label} disabled={disabled}>
          <option value="">Choose a course</option>
          {groups.map((g) => (
            <optgroup key={g.category} label={CATEGORY_LABELS[g.category]}>
              {g.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.current_version ? c.title : `${c.title} (not published yet)`}
                </option>
              ))}
            </optgroup>
          ))}
        </FilterSelect>
      )}
    </div>
  );
}

export default CoursePicker;
